/* eslint-disable @typescript-eslint/no-explicit-any */
import { templates } from "@/components/templates";
import { appLocales, Locales } from "@/lib/i18n";
import { segments, actions } from "@/locales/segment";
import chalk from "chalk";

import { decodeRouteSegments } from "./route-segments";

export const getTemplate = (template: string, isArchive = false): Template => {
  if (process.env?.NODE_ENV === "development") {
    console.info(chalk.green(`Template :`), chalk.yellow(`${template}`));
  }

  let templateComponent = null;

  if (isArchive) {
    if (templates["archive-" + template]) {
      templateComponent = templates["archive-" + template];
    }
  }

  if (!templateComponent) templateComponent = templates[template];

  if (!templateComponent) {
    if (process.env?.NODE_ENV === "development") console.error(chalk.red(`Template not found: ${template}`));
    return templates["default"];
  }

  return templateComponent;
};

export function resolveSegment(locale: Locales, segment: string, findInAction = false, findType = false) {
  const allSegments = findInAction ? actions : segments;

  if (!findType) {
    if (allSegments[locale]?.[segment]) {
      return allSegments[locale][segment];
    }

    return undefined;
  }

  return (
    Object.keys(allSegments[locale]).find((key) => {
      return allSegments[locale][key] === segment;
    }) || undefined
  );
}

export const resolveSegmentKey = (locale: Locales, segment: string, findInAction = false) => {
  const allSegments: any = findInAction ? actions : segments;

  // Find in all values of the segments object the segment
  // that matches the one passed as an argument

  let key = "";

  Object.keys(allSegments).forEach((locale: string) => {
    const found = Object.keys(allSegments[locale]).find((key) => allSegments[locale][key] === segment);

    if (found) {
      key = found;
    }
  });

  if (!key) return "";

  // If key, find the value in allSegments for locale
  // and return the key
  const value = allSegments[locale][key];

  if (value === segment) {
    return key;
  }

  return value;
};

export const resolveTemplate = async (params: string[]): Promise<Resolution> => {
  // Handle empty params case
  if (!params || params.length === 0) {
    return {
      locale: appLocales.defaultLocale as Locales,
      templateName: "homepage",
      Template: getTemplate("homepage"),
      slugs: [],
      isArchive: false,
      isStatic: false,
    };
  }

  const routeParams = decodeRouteSegments(params);

  // Determine if the first param is a locale
  const firstParam = routeParams[0];
  const isFirstParamLocale = appLocales.locales.includes(firstParam as Locales);

  // Set locale and adjust params array if needed
  const locale = isFirstParamLocale ? (firstParam as Locales) : (appLocales.defaultLocale as Locales);
  const adjustedParams = isFirstParamLocale ? routeParams.slice(1) : routeParams;

  if (adjustedParams[0] === "auth") {
    if (adjustedParams[1] === "google-redirect") {
      return {
        locale: appLocales.defaultLocale as Locales,
        templateName: "google-callback",
        Template: getTemplate("google-callback"),
        slugs: adjustedParams,
        isArchive: false,
        isStatic: false,
      };
    }
  }

  // If adjusted params is empty, it's the homepage
  if (adjustedParams.length === 0) {
    return {
      locale,
      templateName: "homepage",
      Template: getTemplate("homepage"),
      slugs: [],
      isArchive: false,
      isStatic: false,
    };
  }

  // Try to resolve the template from the first adjusted param
  const template = resolveSegment(locale, adjustedParams[0], false, true);

  // Handle different cases
  if (adjustedParams.length === 1) {
    const isArchive =
      template === "product" ||
      template === "account" ||
      template === "login" ||
      template === "inspiration" ||
      template === "inspiration_archive" ||
      template === "tables" ||
      template === "catalogue";

    return {
      locale,
      templateName: template ? template : "page",
      Template: getTemplate(template ? template : "page", isArchive),
      slugs: adjustedParams,
      isArchive,
      isStatic: template === "account",
    };
  }

  if (template) {
    // Special handling for inspiration_archive with a slug ( it's a inpiration category )
    if (template === "inspiration_archive" && adjustedParams.length === 2) {
      return {
        locale,
        templateName: "inspiration_category",
        Template: getTemplate("inspiration_category"),
        slugs: [adjustedParams[1]], // The specific inspiration category slug
        isArchive: true,
        isStatic: false,
      };
    }

    // Special handling for inspiration_archive with more than one slug ( it's a single inspiration )
    if (template === "inspiration_archive" && adjustedParams.length === 3) {
      return {
        locale,
        templateName: "inspiration",
        Template: getTemplate("inspiration"),
        slugs: adjustedParams.slice(-2), // The inspiration category slug + inspiration slug
        isArchive: false,
        isStatic: false,
      };
    }

    // Special handling for account with sub-routes (like orders/[orderid])
    if (template === "account") {
      return {
        locale,
        templateName: template,
        Template: getTemplate(template),
        slugs: adjustedParams.slice(1), // This will include both the action and order ID
        isArchive: true,
        isStatic: false,
      };
    }

    if (template === "search") {
      return {
        locale,
        templateName: "search",
        Template: getTemplate("search"),
        slugs: adjustedParams,
        isArchive: false,
        isStatic: false,
      };
    }

    return {
      locale,
      templateName: template,
      Template: getTemplate(template),
      slugs: adjustedParams.slice(1),
      isArchive: false,
      isStatic: template === "account",
    };
  }

  // Default case
  return {
    locale,
    templateName: "page",
    Template: getTemplate("page"),
    slugs: adjustedParams,
    isArchive: false,
    isStatic: false,
  };
};
